import {
  halfANameArrived,
  partitionPrefill,
  requiredStillEmpty,
  type CreateClientField,
  type CreateClientRequiredField,
  type PartitionedPrefill,
} from "@/features/clients/lib/create-client-prefill";
import { validatePersonnummer } from "@/domain/clients/personnummer";
import { wireText } from "@/shared/forms/wire-value";
import type { NewClientField } from "@/features/advice-portal/lib/new-client";
import type { NewClientFill } from "@/features/advice-portal/lib/new-client-fill";
import type { HandlerOutcome } from "@/features/intelligence/lib/frontend-tool-run";
import type { OpenCreateClientArgs } from "@/features/intelligence/lib/frontend-tools";

export interface OpenCreateClientPlan {
  prefill: Record<string, unknown>;
  partition: PartitionedPrefill;
  numberRefused: boolean;
  missingRequired: CreateClientRequiredField[];
  halfAName: boolean;
}

export function planOpenCreateClient(args: OpenCreateClientArgs): OpenCreateClientPlan {
  const prefill: Record<string, unknown> = {};
  for (const [wire, field] of Object.entries(WIRE_TO_FIELD)) {
    const value = wireText(args[wire as keyof OpenCreateClientArgs]);
    if (value !== null) prefill[field] = value;
  }
  for (const [wire, key] of Object.entries(COMPANY_WIRE)) {
    const value = wireText(args[wire as keyof OpenCreateClientArgs]);
    if (value !== null) prefill[key] = value;
  }
  const other = wireText(args.other_details);
  if (other !== null) prefill[OTHER_DETAILS_KEY] = other;

  const number = wireText(args.personnummer);
  const partition = partitionPrefill(prefill);
  return {
    prefill,
    partition,
    numberRefused: number !== null && !validatePersonnummer(number).ok,
    missingRequired: requiredStillEmpty(partition.fields),
    halfAName: halfANameArrived(partition.fields),
  };
}

function isCreateClientPlanEmpty(plan: OpenCreateClientPlan): boolean {
  return plan.partition.marked.length === 0;
}

const NOTHING_CREATED =
  "NOTHING HAS BEEN CREATED. No person, no client and no portfolio exists as a " +
  "result of this: the form is only on screen, and the advisor must check it " +
  "and confirm twice before anything is saved — a saved person cannot be " +
  "deleted afterwards. Do not tell the advisor that a client has been added, " +
  "registered or created, and do not say the details are correct: you have not " +
  "checked them, they have not been checked, and the boxes you filled are " +
  "marked on screen for the advisor to check.";

export function reportOpenCreateClient(
  plan: OpenCreateClientPlan,
  opened: boolean,
): HandlerOutcome {
  if (!opened) {
    return {
      ok: false,
      message:
        "The new-client form was already open on the advisor's screen, so nothing " +
        "was filled in and nothing was changed: replacing what they are working on " +
        "is not something this can do. Tell the advisor the form is already open, " +
        "and ask them to close it first if they want it filled from this " +
        "document. " +
        NOTHING_CREATED,
    };
  }

  if (isCreateClientPlanEmpty(plan)) {
    const carried = plan.partition.unstorable.length > 0;
    return {
      ok: true,
      message:
        "The new-client form is now open on the advisor's screen, and it is EMPTY: " +
        (carried
          ? "nothing in what you passed is something this form can save, so no box " +
            "was filled. What you passed is shown to the advisor as details that " +
            "will not be saved here. "
          : "no details were passed, so no box was filled. ") +
        "The advisor fills it in themselves. " +
        NOTHING_CREATED,
    };
  }

  const filled = plan.partition.marked.map(fieldWord).join(", ");
  const unstorable =
    plan.partition.unstorable.length > 0
      ? " The form ALSO shows the advisor what you passed that it cannot save — a " +
        "birth date, employment, household, a segment, a portfolio or its holdings " +
        "are not part of creating a person here and no amount of filling in changes " +
        "that. Do not tell the advisor those were entered."
      : "";
  const number = plan.numberRefused
    ? " ⚠️ The personnummer you passed is NOT a valid Swedish personnummer and the " +
      "form has refused it: it is in the box, shown as wrong, and the advisor " +
      "cannot save until it is corrected. Say so — do not describe the form as " +
      "ready."
    : "";

  return {
    ok: true,
    message:
      `The new-client form is now open on the advisor's screen with these boxes ` +
      `filled in from what you passed: ${filled}. Every one of them is marked as ` +
      `coming from the document and is still editable.${unstorable}${number}` +
      stillNeeded(plan) +
      " " +
      NOTHING_CREATED,
  };
}

export function reportFillNewClientPage(
  plan: OpenCreateClientPlan,
  fill: NewClientFill,
): HandlerOutcome {
  const head =
    fill.filled.length > 0
      ? "The new-client page on the advisor's screen now has these boxes filled in " +
        `from what you passed: ${listed(fill.filled.map(pageFieldWord))}. Each is ` +
        "marked as coming from the document and is still editable."
      : "Nothing was filled in on the new-client page on the advisor's screen: " +
        (plan.partition.marked.length === 0
          ? "nothing you passed is something this page has a box for."
          : "every box you passed already holds a value there.");
  const kept =
    fill.kept.length > 0
      ? " These boxes already held a different value on the advisor's screen and " +
        `were left as they were: ${listed(fill.kept.map(pageFieldWord))}. This never ` +
        "replaces what is on the page; if the document says otherwise, tell the " +
        "advisor what it says and let them change it."
      : "";
  const unstorable = plan.partition.unstorable.some((value) => !COMPANY_KEYS.has(value.path))
    ? " What you passed that this page has no box for — a birth date, employment, " +
      "household, a segment, a portfolio or its holdings — is NOT shown anywhere " +
      "on it and will not be saved. Do not tell the advisor those were entered."
    : "";

  return {
    ok: fill.filled.length > 0,
    message:
      head +
      kept +
      unstorable +
      pageWrong(fill.wrong) +
      pageStillNeeded(plan, fill) +
      " " +
      NOTHING_CREATED,
  };
}

const WIRE_TO_FIELD: Readonly<Record<string, CreateClientField>> = {
  first_name: "firstName",
  middle_name: "middleName",
  last_name: "lastName",
  personnummer: "personnummer",
  email: "email",
  phone: "phone",
  address_line_1: "addressLine1",
  address_line_2: "addressLine2",
  zip_code: "zipCode",
  city: "city",
};

const COMPANY_WIRE: Readonly<Record<string, string>> = {
  company_name: "companyName",
  organisation_number: "orgNumber",
};

const COMPANY_KEYS: ReadonlySet<string> = new Set(Object.values(COMPANY_WIRE));

const OTHER_DETAILS_KEY = "otherDetails";

function stillNeeded(plan: OpenCreateClientPlan): string {
  if (plan.missingRequired.length === 0) return "";

  const words = plan.missingRequired.map(fieldWord);
  const list =
    words.length === 1
      ? words[0]
      : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
  const isAre = words.length === 1 ? "is" : "are";

  const half = plan.halfAName
    ? " You were given a name and passed only half of it: a Swedish name in the " +
      "advisor's own sentence is yours to split into a first name and a surname, " +
      "and dropping the half you were less sure of leaves a box the advisor may " +
      "not notice is empty. Tell them which half you had and which you did not."
    : "";

  const pnr = plan.missingRequired.includes("personnummer")
    ? " Ask the advisor for the personnummer; do NOT invent, complete or guess " +
      "one, and do not offer a value of your own for them to confirm."
    : "";

  return (
    ` ⚠️ THE FORM CANNOT BE SAVED AS IT STANDS: ${list} ${isAre} still empty, and ` +
    `"Skapa kund" stays disabled until the advisor fills ${
      words.length === 1 ? "it" : "them"
    } in. Say which box is empty and ask the advisor for the value.${half}${pnr} ` +
    "Do NOT call this tool again to add it: the form is open now and a second " +
    "call will not replace what is on their screen."
  );
}

function fieldWord(field: CreateClientField): string {
  const words: Record<CreateClientField, string> = {
    firstName: "first name",
    middleName: "middle name",
    lastName: "last name",
    personnummer: "personnummer",
    email: "email",
    phone: "phone",
    addressLine1: "address",
    addressLine2: "second address line",
    zipCode: "postcode",
    city: "city",
  };
  return words[field];
}

function pageWrong(wrong: NewClientField[]): string {
  if (wrong.length === 0) return "";
  const one = wrong.length === 1;
  return (
    ` ⚠️ The ${listed(wrong.map(pageFieldWord))} you passed ${one ? "is" : "are"} NOT ` +
    `valid: ${one ? "it is" : "they are"} in the box, shown as wrong, and the advisor ` +
    `cannot create the contact until ${one ? "it is" : "they are"} corrected. Say so — ` +
    "do not describe the page as ready."
  );
}

function pageStillNeeded(plan: OpenCreateClientPlan, fill: NewClientFill): string {
  if (fill.missing.length === 0) return "";
  const one = fill.missing.length === 1;
  const half =
    plan.halfAName && fill.missing.some((field) => field === "firstName" || field === "lastName")
      ? " You were given a name and passed only half of it: a Swedish name in the " +
        "advisor's own sentence is yours to split into a first name and a surname. " +
        "Tell them which half you had and which you did not."
      : "";
  const pnr = fill.missing.includes("personnummer")
    ? " Ask the advisor for the personnummer; do NOT invent, complete or guess " +
      "one, and do not offer a value of your own for them to confirm."
    : "";
  return (
    ` ⚠️ THE CONTACT CANNOT BE CREATED AS IT STANDS: ${listed(
      fill.missing.map(pageFieldWord),
    )} ${one ? "is" : "are"} still empty, and "${fill.createLabel}" stays disabled ` +
    `until the advisor fills ${one ? "it" : "them"} in. Say which box is empty and ` +
    `ask the advisor for the value.${half}${pnr} If they give it to you, you may ` +
    "call this tool again with it: a call fills only empty boxes and never replaces " +
    "a value already on the page."
  );
}

function listed(words: string[]): string {
  return words.length === 1
    ? words[0]
    : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function pageFieldWord(field: NewClientField): string {
  const words: Record<NewClientField, string> = {
    firstName: "first name",
    lastName: "last name",
    personnummer: "personnummer",
    name: "company name",
    orgNumber: "organisation number",
    addressLine1: "address",
    zipCode: "postcode",
    city: "city",
    phone: "phone",
    email: "email",
  };
  return words[field];
}
